import * as React from "react";
import MUIDataTable, {
  MUIDataTableMeta,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { Button, Card, TextField, Box } from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SlideOverPanel from "components/SlideOverPanel";
import { AxiosError } from "axios";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface StatusCellProps {
  value: string;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => {
  if (!value || typeof value !== "string") return <>-</>;

  const formatted = value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .replace("In Use No Subscription", "In Use (No Subscription)")
    .replace("Out Of Maintenance", "Out of Maintenance")
    .replace("Out Of Use", "Out of Use")
    .replace("Not Installed", "Not Installed");

  return <span>{formatted}</span>;
};
interface ActionsCellProps {
  subscription: SubscriptionProps;
  onRenew: (subscription: SubscriptionProps) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ subscription, onRenew }) => {
  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={() => onRenew(subscription)}
    >
      Renew
    </Button>
  );
};

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};
interface SubscriptionProps {
  duration_in_days: number;
  subscription_id: number;
  asset_id: number;
  asset_status: string;
  name?: string;
  asset_service_id?: string;
  asset_name?: string;
  subscription_type_name?: string;
  start_date?: string;
  end_date?: string;
  [key: string]: unknown;
}

const todayStr = () => new Date().toISOString().split("T")[0];
const addDays = (dateStr: string, days: number) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const RenewalList = () => {
  const [rows, setRows] = React.useState<SubscriptionProps[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [slideOverOpen, setSlideOverOpen] = React.useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    React.useState<SubscriptionProps | null>(null);
  const [renewalStartDate, setRenewalStartDate] = React.useState(todayStr());
  const [renewalEndDate, setRenewalEndDate] = React.useState(() =>
    addDays(todayStr(), 30)
  );
  const [submitting, setSubmitting] = React.useState(false);
  const { fetchSuccess, fetchError } = useSnackbar();

  const formatDate = (dateString: string | number | Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const openRenewalPanel = (subscription: SubscriptionProps) => {
    const start = todayStr();
    const durationDays = subscription.duration_in_days || 30;
    setSelectedSubscription(subscription);
    setRenewalStartDate(start);
    setRenewalEndDate(addDays(start, durationDays));
    setSlideOverOpen(true);
  };

  const closeRenewalPanel = () => {
    setSlideOverOpen(false);
    setSelectedSubscription(null);
  };

  const handleRenewalSubmit = async () => {
    if (!selectedSubscription) return;
    setSubmitting(true);
    try {
      await axios.put(`/subscription/${selectedSubscription.subscription_id}`, {
        start_date: renewalStartDate,
        end_date: renewalEndDate,
        is_active: 1,
      });
      await axios.put(`/assets/${selectedSubscription.asset_id}`, {
        asset_status: "in_use",
      });
      fetchSuccess("Subscription renewed successfully");
      closeRenewalPanel();
      fetchData();
    } catch (err) {
      console.error("Error renewing subscription:", err);
      const data = (err as AxiosError<{ error?: string }>)?.response?.data;
      const msg = data?.error ?? "Failed to renew subscription";
      fetchError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (!selectedSubscription || !slideOverOpen) return;
    const start = todayStr();
    const durationDays = selectedSubscription.duration_in_days || 30;
    setRenewalStartDate(start);
    setRenewalEndDate(addDays(start, durationDays));
  }, [selectedSubscription?.subscription_id, slideOverOpen]);
  const renderStatusCell = (value: string) => {
    return <StatusCell value={value} />;
  };

  const renderActionsCell = (value: any, tableMeta: MUIDataTableMeta) => {
    const meta = tableMeta as MUIDataTableMeta & {
      rowData?: unknown[];
      currentTableData?: Array<{ index: number }>;
      tableData?: SubscriptionProps[];
    };
    let subscription: SubscriptionProps | undefined;
    if (meta?.rowData && typeof meta.rowData[7] !== "undefined") {
      subscription = rows.find((r) => r.subscription_id === meta.rowData![7]);
    }
    if (!subscription && meta?.tableData && meta?.currentTableData) {
      subscription =
        meta.tableData[meta.currentTableData[meta.rowIndex]?.index];
    }
    if (!subscription && rows[meta?.rowIndex]) {
      subscription = rows[meta.rowIndex];
    }
    if (!subscription?.subscription_id) return null;
    return (
      <ActionsCell subscription={subscription} onRenew={openRenewalPanel} />
    );
  };

  const columns = [
    { name: "name", label: "Customer" },
    { name: "asset_service_id", label: "Asset Id" },
    { name: "asset_name", label: "Brand Name" },
    { name: "subscription_type_name", label: "Subscription Type" },
    {
      name: "start_date",
      label: "Start Date",
      options: {
        filter: false,
        customBodyRender: (value: string) => formatDate(value),
      },
    },
    {
      name: "end_date",
      label: "End Date",
      options: {
        filter: false,
        customBodyRender: (value: string) => formatDate(value),
      },
    },
    {
      name: "asset_status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: renderStatusCell,
      },
    },
    {
      name: "subscription_id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/subscription/expired");
      setRows(response?.data);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets m-category"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>

      <SlideOverPanel
        open={slideOverOpen}
        onClose={closeRenewalPanel}
        title="Renew subscription"
        width={520}
      >
        {selectedSubscription && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                Customer
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                value={selectedSubscription.name ?? ""}
                variant="outlined"
                disabled
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                Asset
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                value={
                  [selectedSubscription.asset_service_id, selectedSubscription.asset_name]
                    .filter(Boolean)
                    .join(" – ") || "-"
                }
                variant="outlined"
                disabled
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                Subscription type
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                value={selectedSubscription.subscription_type_name ?? ""}
                variant="outlined"
                disabled
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                Start date
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={renewalStartDate}
                onChange={(e) => setRenewalStartDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                End date
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={renewalEndDate}
                onChange={(e) => setRenewalEndDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </SoftBox>
            <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                className="success-btn"
                onClick={handleRenewalSubmit}
                disabled={submitting}
              >
                {submitting ? "Renewing…" : "Submit renewal"}
              </Button>
              <Button
                variant="outlined"
                className="cancel-btn"
                onClick={closeRenewalPanel}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default RenewalList;
